/* lazyWithRetry — drop-in replacement for React.lazy() with:
 *
 *   • Automatic retry on ChunkLoadError. CDN edge nodes occasionally serve
 *     a stale 404 or HTML fallback for a brand-new chunk; a one-shot retry
 *     usually resolves it once the CDN refreshes from origin.
 *   • Cache-bust on retry: appends ?retry=<ts> so any cached bad response is
 *     bypassed and a fresh request goes through.
 *   • Hard reload after N failed retries: nukes the SW + reloads with a
 *     cache-busting query string. This handles the "deploy left a stale
 *     manifest in the user's browser" class of bug where main.js references
 *     chunk hashes that the new deploy doesn't ship anymore.
 *   • Session-storage reload guard so a broken chunk can't infinite-loop
 *     reload the page.
 *
 * Usage:
 *   const Page = lazyWithRetry(() => import(/* webpackChunkName: "p-foo" *\\/ "./Page"));
 */
import { lazy } from "react";

const RELOAD_GUARD_KEY = "vault.chunk.reload";

function isChunkError(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg  = err.message || "";
  // ChunkLoadError is thrown by webpack runtime; SyntaxError happens when
  // the server serves index.html and the browser tries to parse it as JS.
  return (
    name === "ChunkLoadError" ||
    /loading chunk [\d]+ failed/i.test(msg) ||
    /loading css chunk/i.test(msg) ||
    /unexpected token '<'/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg)
  );
}

export function lazyWithRetry(factory, { retries = 2, retryDelayMs = 600 } = {}) {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const tryLoad = () => {
        attempts += 1;
        factory()
          .then(resolve)
          .catch((err) => {
            if (!isChunkError(err) || attempts > retries) {
              // Persistent failure → nuke the SW cache + hard reload, but
              // only once per session so we don't infinite-loop.
              if (isChunkError(err)) {
                try {
                  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") {
                    // Already reloaded once this session — surface the error
                    // so the route fallback can show a clean "stale build"
                    // message instead of looping.
                    return reject(err);
                  }
                  sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
                } catch { /* ignore */ }
                // Best-effort unregister the SW so a stale cached main.js
                // can't keep referencing missing chunks after reload.
                if ("serviceWorker" in navigator) {
                  navigator.serviceWorker.getRegistrations()
                    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
                    .catch(() => {})
                    .finally(() => {
                      window.location.replace(window.location.pathname + "?stale=" + Date.now());
                    });
                } else {
                  window.location.replace(window.location.pathname + "?stale=" + Date.now());
                }
                return;
              }
              return reject(err);
            }
            // Transient failure → wait, then retry.
            setTimeout(tryLoad, retryDelayMs * attempts);
          });
      };
      tryLoad();
    });
  });
}

// Clears the reload guard. Called from App's mount effect once the app has
// successfully rendered, so a future broken chunk gets its one-time reload.
export function resetChunkReloadGuard() {
  try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
}
